import { useEffect, useMemo, useRef } from 'react';
import { getUserArBranches, mergeBranchLists } from '@dill/dill-shared';
import { useAppDispatch, useAppSelector } from '../../redux/store';
import { getUserCustomers, recursiveGetUserCustomers } from '../../redux/services/suppliersService';

const PAGE_SIZE = 100;

/**
 * The supplier's branch list, derived from its customers' `branch` values.
 *
 * `SupplierAdditionalDetails.branches` is only ever hand-entered in Branch Settings and is incomplete for most
 * suppliers, so the customers themselves are the only reliable source. That means the full customer
 * list has to be loaded — this pages through it once per supplier and reuses whatever the store
 * already holds, but on a large supplier the first load is not cheap.
 *
 * Branches currently assigned to users are always included even if no customer carries them any
 * more, so an existing assignment can never silently vanish from the UI.
 *
 * @param options.loadAllCustomers pass false to derive only from customers already in the store,
 * without triggering a full fetch.
 */
export const useSupplierBranchOptions = ({
  loadAllCustomers = true,
}: { loadAllCustomers?: boolean } = {}) => {
  const dispatch = useAppDispatch();
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
  const { customers, customersSupplierId, supplierUsers } = useAppSelector(
    (state) => state.supplier
  );
  const supplierId = currentSupplier?.id;

  // Guards against re-running the (expensive) full page-through for a supplier we already walked,
  // including across remounts of whichever component uses the hook.
  const pagedSupplierIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!loadAllCustomers || !supplierId) return;
    if (pagedSupplierIdRef.current === supplierId) return;
    pagedSupplierIdRef.current = supplierId;

    let cancelled = false;
    const loadEveryCustomer = async () => {
      const firstPage = await dispatch(
        getUserCustomers({
          supplierId,
          previousLastDocumentId: '',
          pageSize: PAGE_SIZE,
          sortBy: 'name',
          sortOrder: 'asc',
        })
      );
      if (cancelled || firstPage.type !== 'supplier/getUserCustomers/fulfilled') return;

      let lastDocumentId = (firstPage.payload as any)?.lastDocumentId as string | undefined;
      let fetched = ((firstPage.payload as any)?.data as unknown[] | undefined)?.length ?? 0;

      while (!cancelled && lastDocumentId && fetched >= PAGE_SIZE) {
        const page = await dispatch(
          recursiveGetUserCustomers({
            supplierId,
            previousLastDocumentId: lastDocumentId,
            pageSize: PAGE_SIZE,
            sortBy: 'name',
            sortOrder: 'asc',
          })
        );
        if (cancelled || page.type !== 'supplier/recursiveGetUserCustomers/fulfilled') return;
        const nextLastDocumentId = (page.payload as any)?.lastDocumentId as string | undefined;
        fetched = ((page.payload as any)?.data as unknown[] | undefined)?.length ?? 0;
        if (!nextLastDocumentId || nextLastDocumentId === lastDocumentId) return;
        lastDocumentId = nextLastDocumentId;
      }
    };

    loadEveryCustomer();
    return () => {
      cancelled = true;
    };
  }, [supplierId, loadAllCustomers, dispatch]);

  const branchOptions = useMemo(() => {
    // Only trust the customers list when it belongs to the supplier we're looking at.
    const relevantCustomers = customersSupplierId === supplierId ? customers : [];
    const fromCustomers = relevantCustomers.map((customer) => customer.buyer);
    const fromAssignments = (supplierUsers ?? []).flatMap((supplierUser) =>
      getUserArBranches({ user: supplierUser, supplierId })
    );
    return mergeBranchLists(
      fromCustomers.map((buyer) => buyer?.branch ?? ''),
      fromAssignments
    );
  }, [customers, customersSupplierId, supplierId, supplierUsers]);

  const isLoadingBranches = useAppSelector((state) =>
    state.supplier.loadingList.includes('getUserCustomers')
  );

  return { branchOptions, isLoadingBranches };
};
